import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { getAuditLogs } from '@/api/alerts'

const actionConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  acknowledge: { icon: EyeIcon, color: 'text-blue-500', label: 'Vu' },
  resolve: { icon: CheckCircleIcon, color: 'text-green-500', label: 'Résolu' },
  create: { icon: PlusCircleIcon, color: 'text-emerald-500', label: 'Créé' },
  update: { icon: PencilSquareIcon, color: 'text-yellow-500', label: 'Modifié' },
  delete: { icon: TrashIcon, color: 'text-red-500', label: 'Supprimé' },
}

const resourceTypeLabels: Record<string, string> = {
  alert: 'Alerte',
  alert_rule: 'Règle',
  operator: 'Opérateur',
  user: 'Utilisateur',
}

interface AuditLogPanelProps {
  resourceType?: string
  resourceId?: string
  limit?: number
}

export function AuditLogPanel({ resourceType, resourceId, limit = 50 }: AuditLogPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['audit-logs', resourceType, resourceId, limit],
    queryFn: () => getAuditLogs({ resource_type: resourceType, resource_id: resourceId, limit }),
    refetchInterval: 30000,
  })

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDetails = (details: Record<string, unknown> | null) => {
    if (!details) return null

    const entries = Object.entries(details)
    if (entries.length === 0) return null

    return (
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {entries.map(([key, value]) => {
          if (key === 'changes' && typeof value === 'object') {
            const changes = value as Record<string, { from: unknown; to: unknown }>
            return Object.entries(changes).map(([field, change]) => (
              <span key={field} className="block">
                {field}: {String(change.from)} → {String(change.to)}
              </span>
            ))
          }
          if (key === 'notes' && value) {
            return <span key={key} className="block italic">"{String(value)}"</span>
          }
          if (key === 'name') {
            return <span key={key} className="block font-medium">{String(value)}</span>
          }
          return null
        })}
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
      >
        <div className="flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Journal d'audit
          </h3>
          <span className="px-2 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
            {logs.length}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Chargement...</div>
          ) : logs.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              Aucune action enregistrée
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {logs.map((log) => {
                const config = actionConfig[log.action] || {
                  icon: ClockIcon,
                  color: 'text-gray-500',
                  label: log.action,
                }
                const Icon = config.icon

                return (
                  <div
                    key={log.id}
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`mt-0.5 ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-medium ${config.color}`}>
                            {config.label}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {resourceTypeLabels[log.resource_type] || log.resource_type}
                          </span>
                          {!resourceId && (
                            <span className="text-xs text-gray-400 font-mono">
                              #{log.resource_id.slice(0, 8)}
                            </span>
                          )}
                        </div>

                        {/* User info */}
                        <div className="flex items-center gap-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <UserIcon className="w-3.5 h-3.5" />
                          <span>{log.user_fullname || 'Système'}</span>
                        </div>

                        {/* Details */}
                        {formatDetails(log.details)}

                        {/* Timestamp */}
                        <div className="mt-1 text-xs text-gray-400">
                          {formatDate(log.created_at)}
                          {log.ip_address && (
                            <span className="ml-2">({log.ip_address})</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
